import EntityTable from "@mirage/components/entityTable/EntityTable";
import LinkCell from "@mirage/components/entityTable/components/LinkCell";
import { Box } from "@mui/system";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import PlanningAppealsService from "@publicPortal/services/PlanningAppealsService";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import React, { memo, useCallback, useMemo } from "react";
import { SearchResultTableProps } from "./PlanningApplicationsTable";
import { TablePaginationBaseProps } from "@mui/material/TablePagination";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";

const pp = new PublicPortalRoutingService();
const service = new PlanningAppealsService();

const PlanningAppealsTable: React.FC<SearchResultTableProps> = memo(({ filter, title, customPageRequest }) => {
    const { isMobile } = useDeviceDetect();
    const addressColumnDimensions = useMemo(() => (isMobile ? { width: 200, flex: undefined } : undefined), [isMobile]);
    const descriptionColumnDimensions = useMemo(() => (isMobile ? { width: 200, flex: undefined } : undefined), [isMobile]);
    const getRecordDetailsUrl = useCallback((id: number) => pp.PlanningAppealsRecordRoute(id), []);
    const onPageRequest = useCallback(
        async (pr: IEntityPageRequest) => {
            if (customPageRequest) {
                return await customPageRequest(pr);
            }
            const response = await service.pageRequest(pr);
            return response;
        },
        [customPageRequest]
    );
    const columns = useMemo(() => {
        return [
            {
                field: "name",
                headerName: "Appeal Ref No.",
                renderCell: (params: GridRenderCellParams) => <LinkCell value={params.value} route={getRecordDetailsUrl(Number(params.id))} />,
                width: 160,
            },
            { field: "address", headerName: "Address", ...addressColumnDimensions },
            { field: "appeal_grounds", headerName: "Appeal Grounds", width: 140 },
            { field: "appeal_description", headerName: "Appeal Description", ...descriptionColumnDimensions },
            { field: "awarded", headerName: "Awarded", width: 100 },
        ];
    }, [addressColumnDimensions, descriptionColumnDimensions, getRecordDetailsUrl]);
    const overridePaginationProps = useCallback((baseProps: TablePaginationBaseProps) => ({ ...baseProps, showFirstButton: false, showLastButton: false }), []);
    const getRowHeight = useCallback(() => "auto", []);

    return (
        <Box sx={{ height: "auto" }}>
            <EntityTable
                title={title}
                key="planning-appeals-entityTable"
                stateKey="PlanningAppeals"
                onPageRequest={customPageRequest ? customPageRequest : onPageRequest}
                columns={columns}
                filter={filter}
                getRowHeight={getRowHeight}
                hideRowsPerPage={isMobile}
                overridePaginationProps={overridePaginationProps}
                inlineTitle={!isMobile}
            />
        </Box>
    );
});

export default PlanningAppealsTable;
